import { compact, map } from "lodash/fp";
import { DateTime } from "luxon";

import { DateSelection } from "@app/analytics/components/DateSelector/utils";
import { DateRange } from "@app/analytics/meetops/types";
import { CustomDateRange, DateRangePreset } from "@app/analytics/types";

export const getQueryDateRangeFromDateTimes = ({
  startDate,
  endDate,
}: DateRange) => {
  if (!startDate || !endDate) return [];
  return [
    {
      startDate,
      endDate,
      tzName: startDate.zoneName ?? undefined,
    },
  ];
};

export const getQueryDateRangesFromSelection = (
  dateSelection: DateSelection
) => {
  // timezone should be consistent across all date ranges
  const tzName = dateSelection.startDate.zoneName;

  const ranges = [
    {
      startDate: dateSelection.startDate,
      endDate: dateSelection.endDate,
      tzName: tzName ?? undefined,
    },
  ];

  if (
    dateSelection.compare &&
    dateSelection.compareStartDate &&
    dateSelection.compareEndDate
  ) {
    ranges.push({
      startDate: dateSelection.compareStartDate,
      endDate: dateSelection.compareEndDate,
      tzName: tzName ?? undefined,
    });
  }
  return ranges;
};

export const getQueryKeyFromDateRanges = (
  dateRanges?: DateRange[]
): string[] => {
  if (dateRanges == null) {
    return [];
  }

  return map(
    (dr: DateRange) =>
      `${dr.startDate.toFormat("yyyy-MM-dd")}--${dr.endDate.toFormat(
        "yyyy-MM-dd"
      )}`,
    dateRanges
  );
};

const getDatesFromPreset = (
  preset: DateRangePreset,
  endDate = DateTime.now()
): DateRange => {
  const days = parseInt(preset);

  let periodStart;

  if (days > 30) {
    periodStart = endDate.minus({ months: days / 30 }).startOf("day");
  } else {
    periodStart = endDate.minus({ days }).startOf("day");
  }

  const periodEnd = endDate.endOf("day");

  return { startDate: periodStart, endDate: periodEnd };
};

const getPrimaryDateRange = (
  dateRangePreset: DateRangePreset,
  customDateRange?: CustomDateRange | null
) => {
  if (customDateRange) {
    const [startDate, endDate] = customDateRange;
    return {
      startDate: startDate.startOf("day"),
      endDate: endDate.endOf("day"),
    };
  }

  return getDatesFromPreset(dateRangePreset);
};

const getCompareDateRange = (dateRange: DateRange, tzName?: string) => {
  const numDaysInRange = Math.ceil(
    dateRange.endDate.diff(dateRange.startDate).as("days")
  );

  const compareEndDate = dateRange.startDate.minus({ days: 1 });

  return {
    startDate: compareEndDate.minus({ days: numDaysInRange - 1 }),
    endDate: compareEndDate.endOf("day"),
    tzName,
  };
};

export const getDateRanges = ({
  customDateRange,
  dateRangePreset,
  comparing = false,
}: {
  customDateRange?: CustomDateRange | null;
  dateRangePreset: DateRangePreset | null;
  comparing?: boolean;
}) => {
  const primaryDateRange = getPrimaryDateRange(
    dateRangePreset ?? "30",
    customDateRange
  );

  // timezone should be consistent across all date ranges
  const tzName = primaryDateRange.startDate.zoneName ?? undefined;

  const compareDateRange = comparing
    ? getCompareDateRange(primaryDateRange, tzName)
    : null;

  return compact([{ ...primaryDateRange, tzName }, compareDateRange]);
};
